import React, { useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { useMutation, useQuery } from '@apollo/client'
import LoadingModal from 'src/components/common/LoadingModal'
import { showNotificationMessage } from 'src/actions'
import { NOTIFICATION_TYPES } from 'src/utils/constants/common'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Button, Checkbox, FormControlLabel } from '@mui/material'
import { COLORS } from 'src/utils/Themes/colors'
import GraphQlErrors from 'src/components/common/GraphQlErrors'
import {
  FETCH_NOTIFICATION_SETTINGS,
  UPDATE_NOTIFICATION_SETTINGS,
} from 'src/services/individualService'
import { NOTIFICATION_SETTINGS_MSG } from 'src/utils/constants/Individual'

const NotificationSettings = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const userInfo = useSelector((state) => state?.userInfo)
  const clientId = userInfo?.userInfo?.userData?.clientId

  const initialValues = {
    id: '',
    policyUploadReminder: false,
    loginReminder: false,
    policyExpiredReminder: false,
    policyExpiringReminder: false,
  }

  const {
    loading: notificationSettingsLoading,
    error: notificationSettingsError,
    data: notificationSettingsData,
    refetch,
  } = useQuery(FETCH_NOTIFICATION_SETTINGS(clientId), {
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  })

  const [raiseRequest, { loading: updateNotificationSettingsLoading }] =
    useMutation(UPDATE_NOTIFICATION_SETTINGS(), {
      onCompleted: () => {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.SUCCESS,
            message: NOTIFICATION_SETTINGS_MSG?.NOTIFICATION_SETTINGS_UPDATED,
          })
        )
        refetch()
      },
      onError: () => {
        dispatch(
          showNotificationMessage({
            type: NOTIFICATION_TYPES?.ERROR,
            message: NOTIFICATION_SETTINGS_MSG?.ENCOUNTERED_ERROR,
          })
        )
      },
    })

  const setDefaultFormValues = () => {
    const notificationSettings =
      notificationSettingsData?.fetchClientsNotificationsSettingsData
        ?.clientNotificationSettings
    if (notificationSettings) {
      settingsForm.setValues(
        {
          id: notificationSettings?.id,
          policyUploadReminder: notificationSettings?.policyUploadReminder,
          loginReminder: notificationSettings?.loginReminder,
          policyExpiredReminder: notificationSettings?.policyExpiredReminder,
          policyExpiringReminder: notificationSettings?.policyExpiringReminder,
        },
        false
      )
    }
  }

  useEffect(() => {
    setDefaultFormValues()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationSettingsData])

  const settingsForm = useFormik({
    initialValues: initialValues,
    validationSchema: Yup.object({
      id: Yup.string(),
      policyUploadReminder: Yup.boolean(),
      loginReminder: Yup.boolean(),
      policyExpiredReminder: Yup.boolean(),
      policyExpiringReminder: Yup.boolean(),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true)
      handleSubmit(values)
    },
    enableReinitialize: true,
  })

  const getId = (idValue) => {
    if (idValue && idValue?.length) {
      return idValue
    }
    return null
  }

  const handleSubmit = (values) => {
    raiseRequest({
      variables: {
        notificationsSettings: {
          id: getId(values?.id),
          clientId,
          policyUploadReminder: values?.policyUploadReminder,
          loginReminder: values?.loginReminder,
          policyExpiredReminder: values?.policyExpiredReminder,
          policyExpiringReminder: values?.policyExpiringReminder,
        },
      },
    })
  }

  if (notificationSettingsError) {
    return <GraphQlErrors error={notificationSettingsError} />
  }

  const handleFormChange = (event) => {
    const { name, checked } = event?.target
    settingsForm.setFieldValue(name, checked, true)
  }

  return (
    <>
      <LoadingModal
        loading={
          notificationSettingsLoading || updateNotificationSettingsLoading
        }
      />
      <div className={classes.settingsContainer}>
        <div className={classes.settingHeading}>Notification Settings</div>
        <div className={classes.settingFormContainer}>
          <FormControlLabel
            control={
              <Checkbox
                id='loginReminder'
                name='loginReminder'
                checked={settingsForm?.values?.loginReminder}
                onChange={handleFormChange}
              />
            }
            label='Login Reminder'
            className={classes.loginReminderField}
          />
          <FormControlLabel
            control={
              <Checkbox
                id='policyUploadReminder'
                name='policyUploadReminder'
                checked={settingsForm?.values?.policyUploadReminder}
                onChange={handleFormChange}
              />
            }
            label='Policy Upload Reminder'
            className={classes.loginReminderField}
          />
          <FormControlLabel
            control={
              <Checkbox
                id='policyExpiringReminder'
                name='policyExpiringReminder'
                checked={settingsForm?.values?.policyExpiringReminder}
                onChange={handleFormChange}
              />
            }
            label='Policy Expiring Reminder'
            className={classes.loginReminderField}
          />
          <FormControlLabel
            control={
              <Checkbox
                id='policyExpiredReminder'
                name='policyExpiredReminder'
                checked={settingsForm?.values?.policyExpiredReminder}
                onChange={handleFormChange}
              />
            }
            label='Policy Expired Reminder'
            className={classes.loginReminderField}
          />

          <div className={classes.settingsActionsContainer}>
            <Button
              id='cancelButton'
              variant='outlined'
              className={classes.cancelButton}
              onClick={setDefaultFormValues}
            >
              Cancel
            </Button>
            <Button
              id='submitButton'
              variant='contained'
              className={classes.submitButton}
              onClick={() => {
                settingsForm.submitForm()
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

export default NotificationSettings

const useStyles = makeStyles({
  settingsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '10px 0',
  },
  settingHeading: {
    fontSize: '16px',
    fontWeight: 'bold',
  },
  loginReminderField: {
    marginLeft: '0 !important',
    width: 'fit-content !important',
  },
  settingFormContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  settingForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  settingsActionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
  },
  cancelButton: {
    textTransform: 'unset !important',
    borderRadius: '7px',
    color: COLORS.MEDIUM_LIGHTBLUE,
    border: `1px solid ${COLORS.MEDIUM_LIGHTBLUE} !important`,
    '&:hover': {
      borderColor: COLORS.MEDIUM_LIGHTBLUE,
    },
    fontSize: '14px',
    lineHeight: '0.5',
    padding: '4px 10px',
    fontWeight: '600 !important',
  },
  submitButton: {
    textTransform: 'unset !important',
    fontSize: '14px',
    fontWeight: '600 !important',
    background: `${COLORS.DARK_PINK} !important`,
    color: `${COLORS.WHITE} !important`,
    padding: '4px 10px !important',
    borderRadius: '7px !important',
    '&:hover': {
      backgroundColor: COLORS.DARK_PINK,
    },
  },
})
